import { useCallback, useEffect, useRef, useState } from 'react'
import { Bell, Check, Globe, Search, Trash2, Type } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useFontSize, LABELS, type FontSize } from '../contexts/FontSizeContext'

interface NotifItem {
  notif_id: string
  type: string
  title: string
  body: string
  link?: string | null
  is_read: boolean
  created_at: string
}

interface Props {
  title: string
}

export function TopBar({ title }: Props) {
  const { lang, setLang, s } = useI18n()
  const { user } = useCurrentUser()
  const { fontSize, setFontSize, allSizes } = useFontSize()

  const today = new Date()
  const dateStr =
    lang === 'zh'
      ? today.toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
        })
      : today.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
        })

  const toggleLang = () => {
    setLang(lang === 'zh' ? 'en' : 'zh')
  }

  // ─── Notifications dropdown ──────────────────────────────────────────────
  const [showNotif, setShowNotif] = useState(false)
  const [notifications, setNotifications] = useState<NotifItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const loadNotifications = useCallback(() => {
    fetch('/notifications?limit=20')
      .then((r) => r.json())
      .then((data) => {
        if (data.items) setNotifications(data.items)
        if (data.unread_count !== undefined) setUnreadCount(data.unread_count)
      })
      .catch(() => {})
  }, [])

  // Load on mount and poll every 30s
  useEffect(() => {
    loadNotifications()
    const interval = setInterval(loadNotifications, 30000)
    return () => clearInterval(interval)
  }, [loadNotifications])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowNotif(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const markAllRead = () => {
    fetch('/notifications/read', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ all: true }),
    }).then(() => {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
      setUnreadCount(0)
    }).catch(() => {})
  }

  const deleteNotif = (notifId: string) => {
    fetch(`/notifications/${notifId}`, { method: 'DELETE' }).then(() => {
      setNotifications((prev) => prev.filter((n) => n.notif_id !== notifId))
      loadNotifications()
    }).catch(() => {})
  }

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return lang === 'zh' ? '刚刚' : 'Just now'
    if (diffMin < 60) return `${diffMin}${lang === 'zh' ? '分钟前' : 'm ago'}`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}${lang === 'zh' ? '小时前' : 'h ago'}`
    const diffDay = Math.floor(diffHr / 24)
    return `${diffDay}${lang === 'zh' ? '天前' : 'd ago'}`
  }

  return (
    <header className="flex items-center justify-between h-14 px-6 border-b border-brand-200 bg-white flex-shrink-0">
      {/* Left: Title + Date */}
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold text-brand-800">{title}</h1>
        <span className="text-sm text-brand-400 hidden md:inline">
          {dateStr}
        </span>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* Font size */}
        <div className="flex items-center gap-1">
          <Type className="w-4 h-4 text-brand-400" />
          <select
            value={fontSize}
            onChange={(e) => setFontSize(e.target.value as FontSize)}
            className="text-xs rounded border border-brand-200 px-1.5 py-1 bg-white text-brand-700 cursor-pointer"
            title={lang === 'zh' ? '字体大小' : 'Font size'}
          >
            {allSizes.map((s) => (
              <option key={s} value={s}>{LABELS[s]}</option>
            ))}
          </select>
        </div>

        {/* Language toggle */}
        <button
          onClick={toggleLang}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-brand-600 hover:bg-brand-100 transition-colors"
          title={s.toggleLangTitle}
        >
          <Globe className="w-4 h-4" />
          <span>{lang === 'zh' ? '中文' : 'EN'}</span>
        </button>

        {/* Search (placeholder — command palette to be implemented) */}
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-brand-200 text-sm text-brand-400 hover:border-brand-300 hover:text-brand-600 transition-colors min-w-[180px]"
        >
          <Search className="w-4 h-4" />
          <span className="flex-1 text-left">
            {lang === 'zh' ? '搜索案件、知识库...' : 'Search...'}
          </span>
          <kbd className="text-[10px] bg-brand-100 px-1.5 py-0.5 rounded text-brand-500">
            ⌘K
          </kbd>
        </button>

        {/* Notifications */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowNotif(!showNotif)}
            className="relative p-2 rounded-lg text-brand-500 hover:bg-brand-100 transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-500 rounded-full text-[10px] text-white font-medium">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Dropdown */}
          {showNotif && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-brand-200 z-50 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-brand-100">
                <h4 className="text-sm font-semibold text-brand-800">
                  {lang === 'zh' ? '通知' : 'Notifications'}
                  {unreadCount > 0 && (
                    <span className="ml-2 text-xs font-normal text-brand-400">
                      {unreadCount} {lang === 'zh' ? '条未读' : 'unread'}
                    </span>
                  )}
                </h4>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" />
                    {lang === 'zh' ? '全部已读' : 'Mark all read'}
                  </button>
                )}
              </div>

              {/* List */}
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="py-8 text-center text-sm text-brand-400">
                    {lang === 'zh' ? '暂无通知' : 'No notifications'}
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.notif_id}
                      className={`px-4 py-3 border-b border-brand-50 last:border-0 hover:bg-brand-50 transition-colors group/notif ${
                        !notif.is_read ? 'bg-blue-50/30' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {!notif.is_read && (
                              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                            )}
                            <span className="text-xs font-medium text-brand-800 truncate">
                              {notif.title}
                            </span>
                          </div>
                          {notif.body && (
                            <p className="text-xs text-brand-500 mt-0.5 line-clamp-2">
                              {notif.body}
                            </p>
                          )}
                          <span className="text-[10px] text-brand-400 mt-1 block">
                            {formatTime(notif.created_at)}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteNotif(notif.notif_id)}
                          className="p-1 rounded text-brand-300 hover:text-red-500 opacity-0 group-hover/notif:opacity-100 transition-opacity flex-shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User avatar */}
        <div className="flex items-center gap-2 pl-2 border-l border-brand-200">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium">
            {user?.display_name?.charAt(0) ?? '?'}
          </div>
          <div className="hidden md:block">
            <div className="text-sm font-medium text-brand-800">
              {user?.display_name ?? '---'}
            </div>
            <div className="text-[11px] text-brand-400">
              {user?.firm_name ?? ''}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
